// src/admin/tabs/HeroTab.js

import React, { useState, useEffect } from 'react';

const HeroTab = () => {
    // --- STATE ---
    // 1. Hero Content State
    const [content, setContent] = useState({
        heading_part1: '', heading_highlight: '', heading_part2: '',
        subheading: '', cta_text: '', pill_text_1: '', pill_text_2: '',
        partners_text: ''
    });
    
    // 2. Apply Section Content State (New)
    const [applyContent, setApplyContent] = useState({
        badge_text: '',
        main_heading: '',
        highlight_heading: '',
        sub_heading: '',
        description: ''
    });

    const [images, setImages] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [message, setMessage] = useState('');
    const [applyMessage, setApplyMessage] = useState('');

    // --- FETCH DATA ---
    useEffect(() => {
        fetchContent();
        fetchImages();
        fetchApplyContent(); // Fetch Apply Section Data
    }, []);

    const fetchContent = async () => {
        const res = await fetch('/api/hero/content');
        const data = await res.json();
        if (data) setContent(data);
    };

    const fetchApplyContent = async () => {
        const res = await fetch('/api/apply/content');
        const data = await res.json();
        if (data) setApplyContent(data);
    };

    const fetchImages = async () => {
        const res = await fetch('/api/hero/images');
        const data = await res.json();
        setImages(data);
    };

    // --- HANDLERS ---
    const handleContentChange = (e) => {
        setContent({ ...content, [e.target.name]: e.target.value });
    };

    const handleApplyContentChange = (e) => {
        setApplyContent({ ...applyContent, [e.target.name]: e.target.value });
    };

    const saveContent = async (e) => {
        e.preventDefault();
        const res = await fetch('/api/hero/content', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(content)
        });
        if (res.ok) {
            setMessage('Hero text updated successfully!');
            setTimeout(() => setMessage(''), 3000);
        }
    };

    const saveApplyContent = async (e) => {
        e.preventDefault();
        const res = await fetch('/api/apply/content', {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(applyContent)
        });
        if (res.ok) {
            setApplyMessage('Apply Section text updated successfully!');
            setTimeout(() => setApplyMessage(''), 3000);
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('image', file);

        const res = await fetch('/api/hero/images', {
            method: 'POST',
            body: formData
        });

        if (res.ok) {
            fetchImages();
            e.target.value = null;
        }
        setUploading(false);
    };

    const deleteImage = async (id) => {
        if(!window.confirm("Delete this image?")) return;
        await fetch(`/api/hero/images/${id}`, { method: 'DELETE' });
        fetchImages();
    };

    return (
        <div className="space-y-8">
            {/* 1. TEXT CONTENT FORM */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                <h3 className="text-lg font-black text-slate-800 uppercase mb-4">Edit Hero Text</h3>
                <form onSubmit={saveContent} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="col-span-2 md:col-span-1">
                        <label className="text-xs font-bold text-slate-400 uppercase">Heading Part 1</label>
                        <input name="heading_part1" value={content.heading_part1 || ''} onChange={handleContentChange} className="w-full p-2 border rounded font-bold" />
                    </div>
                    <div className="col-span-2 md:col-span-1">
                        <label className="text-xs font-bold text-orange-400 uppercase">Heading Highlight (Orange)</label>
                        <input name="heading_highlight" value={content.heading_highlight || ''} onChange={handleContentChange} className="w-full p-2 border rounded font-bold text-orange-500" />
                    </div>
                    <div className="col-span-2">
                        <label className="text-xs font-bold text-slate-400 uppercase">Heading Part 2</label>
                        <input name="heading_part2" value={content.heading_part2 || ''} onChange={handleContentChange} className="w-full p-2 border rounded font-bold" />
                    </div>
                    <div className="col-span-2">
                        <label className="text-xs font-bold text-slate-400 uppercase">Subheading</label>
                        <textarea name="subheading" value={content.subheading || ''} onChange={handleContentChange} className="w-full p-2 border rounded font-medium h-20" />
                    </div>
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase">CTA Button Text</label>
                        <input name="cta_text" value={content.cta_text || ''} onChange={handleContentChange} className="w-full p-2 border rounded font-bold" />
                    </div>
                    
                    <div>
                        <label className="text-xs font-bold text-slate-400 uppercase">Footer Text (Partners)</label>
                        <input name="partners_text" value={content.partners_text || ''} onChange={handleContentChange} className="w-full p-2 border rounded font-bold" placeholder="e.g. Hiring Partners Include" />
                    </div>

                    <div className="col-span-2 grid grid-cols-2 gap-4 bg-slate-50 p-4 rounded-xl">
                        <div>
                            <label className="text-xs font-bold text-slate-400 uppercase">Pill Text 1</label>
                            <input name="pill_text_1" value={content.pill_text_1 || ''} onChange={handleContentChange} className="w-full p-2 border rounded text-xs" />
                        </div>
                        <div>
                            <label className="text-xs font-bold text-slate-400 uppercase">Pill Text 2</label>
                            <input name="pill_text_2" value={content.pill_text_2 || ''} onChange={handleContentChange} className="w-full p-2 border rounded text-xs" />
                        </div>
                    </div>
                    <div className="col-span-2">
                        <button type="submit" className="bg-slate-900 text-white px-6 py-2 rounded-lg font-bold uppercase text-xs hover:bg-orange-500 transition-colors">Save Hero Changes</button>
                        {message && <span className="ml-4 text-green-500 text-sm font-bold">{message}</span>}
                    </div>
                </form>
            </div>

            {/* 2. IMAGE MANAGEMENT */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100">
                <div className="flex justify-between items-center mb-6">
                    <h3 className="text-lg font-black text-slate-800 uppercase">Background Slider Images</h3>
                    <label className="bg-orange-500 text-white px-4 py-2 rounded-lg font-bold uppercase text-xs cursor-pointer hover:bg-orange-600">
                        {uploading ? 'Uploading...' : '+ Add Image'}
                        <input type="file" className="hidden" accept="image/*" onChange={handleImageUpload} disabled={uploading} />
                    </label>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {images.map((img) => (
                        <div key={img.id} className="relative group rounded-xl overflow-hidden shadow-md">
                            <img src={`${img.image_path}`} alt="Hero Bg" className="w-full h-32 object-cover" />
                            <button 
                                onClick={() => deleteImage(img.id)}
                                className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-md opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"></path></svg>
                            </button>
                        </div>
                    ))}
                    {images.length === 0 && <p className="text-sm text-slate-400 col-span-4 text-center py-4">No custom images uploaded. Using default.</p>}
                </div>
            </div>

            {/* 3. APPLY SECTION CONTENT (NEW ADDITION) */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-slate-100 border-t-4 border-t-orange-500">
                <h3 className="text-lg font-black text-slate-800 uppercase mb-4">Edit Apply Section Content</h3>
                <form onSubmit={saveApplyContent} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="col-span-2">
                        <label className="text-xs font-bold text-green-600 uppercase">Badge Text (Green Pill)</label>
                        <input name="badge_text" value={applyContent.badge_text || ''} onChange={handleApplyContentChange} className="w-full p-2 border rounded font-bold text-green-700 bg-green-50" placeholder="e.g. Hiring for 2026-27 Session" />
                    </div>
                    
                    <div className="col-span-2 md:col-span-1">
                        <label className="text-xs font-bold text-slate-400 uppercase">Main Heading</label>
                        <input name="main_heading" value={applyContent.main_heading || ''} onChange={handleApplyContentChange} className="w-full p-2 border rounded font-bold" placeholder="e.g. Stop Applying to" />
                    </div>
                    
                    <div className="col-span-2 md:col-span-1">
                        <label className="text-xs font-bold text-orange-400 uppercase">Highlight Heading (Gradient)</label>
                        <input name="highlight_heading" value={applyContent.highlight_heading || ''} onChange={handleApplyContentChange} className="w-full p-2 border rounded font-bold text-orange-500" placeholder="e.g. Multiple Schools" />
                    </div>

                    <div className="col-span-2">
                        <label className="text-xs font-bold text-slate-400 uppercase">Sub Heading (Italic)</label>
                        <input name="sub_heading" value={applyContent.sub_heading || ''} onChange={handleApplyContentChange} className="w-full p-2 border rounded italic font-medium" placeholder="e.g. Apply Once through Acadfinity" />
                    </div>

                    <div className="col-span-2">
                        <label className="text-xs font-bold text-slate-400 uppercase">Description Paragraph</label>
                        <textarea name="description" value={applyContent.description || ''} onChange={handleApplyContentChange} className="w-full p-2 border rounded font-medium h-24" placeholder="Full description text..." />
                    </div>

                    <div className="col-span-2">
                        <button type="submit" className="bg-slate-900 text-white px-6 py-2 rounded-lg font-bold uppercase text-xs hover:bg-orange-500 transition-colors">Save Apply Section Changes</button>
                        {applyMessage && <span className="ml-4 text-green-500 text-sm font-bold">{applyMessage}</span>}
                    </div>
                </form>
            </div>
        </div>
    );
};

export default HeroTab;